import { type FC } from 'react'
import { twMerge } from 'tailwind-merge'
import cx from 'classnames'
import { Link } from 'react-router'
import { ChevronRightIcon } from '@heroicons/react/24/outline'
import { type DefaultProps } from '@egw/components'

export interface ContentBlockProps extends DefaultProps {
  /**
   * Title
   */
  title: string
  /**
   * Content
   */
  content: string
  /**
   * Is link enabled
   */
  isLink?: boolean
  /**
   * Is loading
   */
  isLoading?: boolean
  /**
   * Link URL
   */
  linkUrl?: string
  /**
   * Handle on click event
   * @returns void
   */
  onClick?: () => void
}

export const ContentBlock: FC<ContentBlockProps> = ({
  // Custom component properties
  title,
  content,
  isLink,
  linkUrl,
  onClick,
  isLoading,
  // Common default properties for each component
  className,
  id,
  testId,
  ariaLabel,
}) => {
  // Final class names
  const finalClasses = twMerge(
    cx('flex gap-lg mb-xl loadingBar', {
      'cursor-pointer border-b border-light pb-sm': isLink,
      'w-full bg-gradient-to-r from-gray-300/0 via-gray-300/35 to-gray-300/0 bg-[length:200%_100%] animate-[loadingBar_2s_ease-in_infinite]':
        isLoading,
    }),
    className,
  )

  // Common props for both div and Link
  const commonProps = {
    className: finalClasses,
    ...(testId ? { 'data-testid': testId } : {}),
    ...(id ? { id } : {}),
    ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
  }

  // Component content to avoid duplication of code
  const Content = (
    <>
      <div className="flex-1">
        <h2 className="typo-primary-md-semibold text-text-header mb-3xs">{title}</h2>
        <p className="typo-primary-sm-regular text-text-meta">{content}</p>
      </div>
      {isLink ? <ChevronRightIcon className="w-md h-md text-text-header" /> : null}
    </>
  )

  return isLink ? (
    <Link to={linkUrl ?? ''} {...commonProps} onClick={onClick}>
      {Content}
    </Link>
  ) : (
    <div {...commonProps}>{Content}</div>
  )
}
